/**
 * tool-executor.ts — Office.js tool dispatcher.
 *
 * When the backend streams a tool_call event, this module maps the
 * tool name to the actual Excel API function and executes it.
 * All Office.js code runs here — never on the server.
 */

import type { SseToolCallEvent, ToolResultRequest } from "excel-agent-core";
import { TOOL_NAMES } from "excel-agent-core";
import { instanceId, getConnectedWorkbooks } from "../excel/workbook-tracker";

// Import all Excel Office.js API functions
import {
    getRangeAsJsonCompact,
    getRangeAsCsv,
    searchData,
    getAllObjects,
    setCellRange,
    writeCells,
    clearCellRange,
    copyTo,
    modifySheetStructure,
    modifyWorkbookStructure,
    resizeRange,
    modifyObject,
    getWorkbookMetadata,
    getWorksheetById,
    analyzeSheetStructureForLLM,
    importData,
    appendRowsToSheet,
    summarizeWorkbook,
} from "../excel/api";
import { parseRange } from "../excel/dirty-tracker";
import { resizeImage } from "../utils/image-resize";

import { getAttachmentContent, getAttachmentStructuredDataPage } from "../api/chat-api";

/**
 * Execute a tool call received from the backend and return the result.
 * This is the "bridge" between the NestJS agent loop and Excel.
 */
// Debug logging (console only)
function debugLog(msg: string) {
    console.log(`[ToolDebug] ${msg}`);
}

// Type validation for tool arguments — catches the most common LLM tool call format errors.
// Specifically targets the "cells passed as a JSON string" bug observed in session ee2b404a.
function validateCellsParam(toolName: string, cells: any, expectedType: 'object' | 'array'): void {
    if (typeof cells === 'string') {
        throw new Error(
            `${toolName}: 'cells' parameter is a JSON STRING, not a${expectedType === 'object' ? 'n object' : ' 2D array'}. ` +
            `You probably accidentally JSON.stringify()'d the data. ` +
            `Pass cells as ${expectedType === 'object' ? 'a plain object: {"A1": {"value": 1}, "B1": {"formula": "=A1*2"}}' : 'a 2D array: [[{"value": 1}, {"value": 2}]]'} ` +
            `instead of a string.`
        );
    }
    if (cells == null) {
        throw new Error(`${toolName}: 'cells' parameter is missing or null.`);
    }
    if (expectedType === 'object' && (Array.isArray(cells) || typeof cells !== 'object')) {
        throw new Error(
            `${toolName}: 'cells' must be a Record<string, {value?, formula?}>, ` +
            `e.g. {"A1": {"value": 1}, "B1": {"formula": "=A1*2"}}. ` +
            `Got ${Array.isArray(cells) ? 'an array' : typeof cells}.`
        );
    }
    if (expectedType === 'array' && !Array.isArray(cells)) {
        throw new Error(
            `${toolName}: 'cells' must be a 2D array of cell objects, ` +
            `e.g. [[{"value": 1}, {"value": 2}]]. ` +
            `Got ${typeof cells}.`
        );
    }
}

export async function executeTool(
    event: SseToolCallEvent,
    forceOverwrite: boolean = false
): Promise<ToolResultRequest & { error: boolean }> {
    const { toolCallId, toolName, args } = event;
    const p = args as any;
    const targetWorkbookId = p.workbookId;

    debugLog(`▶ CALLING: ${toolName} (${JSON.stringify(args).slice(0, 100)}) [forceOverwrite=${forceOverwrite}]`);
    const startTime = Date.now();

    try {
        let result: unknown;

        // Dispatch to remote workbook if needed.
        // MUST be inside try/catch so executeRemoteTool's rejection (timeout
        // line 608, JSON parse line 620) is caught here and returned as a
        // structured error result rather than escaping as an unhandled
        // rejection — which would leave an orphan tool_call in the DB and
        // brick the session (Patch 4 Layer A).
        if (targetWorkbookId && targetWorkbookId !== instanceId) {
            // Pre-flight: bail immediately if the workbook is already gone.
            // Without this check, the caller blocks for 30s then gets a
            // generic timeout the LLM reads as transient and retries.
            const alive = getConnectedWorkbooks().some(wb => wb.id === targetWorkbookId);
            if (!alive) {
                return {
                    sessionId: "",
                    toolCallId,
                    toolName,
                    result: {
                        success: false,
                        error: {
                            code: "WORKBOOK_CLOSED",
                            message: `Workbook (ID: ${targetWorkbookId}) is no longer available — it was closed. Do not retry tool calls targeting this workbook; inform the user instead.`,
                        },
                    },
                    isError: true,
                    error: true,
                };
            }
            debugLog(`🌐 DISPATCHING REMOTE: ${toolName} to ${targetWorkbookId}`);
            return await executeRemoteTool(targetWorkbookId, event, forceOverwrite);
        }

        // Layer 2: Server-side guardrail blocked this tool call
        if ((p as any)._blocked) {
            return {
                sessionId: "",
                toolCallId,
                toolName,
                result: { success: false, error: (p as any)._blockReason || 'Blocked by guardrail' },
                isError: true,
                error: true,
            };
        }

        // Hard Loop detection — Tier 2 (soft cache) or Tier 3 (hard block).
        // The backend computes loopState from chat history + stream tracker and
        // injects it into the tool_call args. We honor it here by short-circuiting
        // Office.js execution so the side effect is NOT re-applied.
        const loopState = (p as any)._loopState;
        if (loopState?.action === 'cache') {
            // Return the cached previous result + WARNING prefix.
            // The previous side effect is already applied; we just prevent a
            // second execution from compounding state.
            let cachedContent: any;
            try {
                cachedContent = JSON.parse(loopState.cachedResult);
            } catch {
                cachedContent = { text: loopState.cachedResult };
            }
            debugLog(`🔄 LOOP CACHE HIT: ${toolName} (priorCount=${loopState.priorCount}) — returning cached result`);
            return {
                sessionId: "",
                toolCallId,
                toolName,
                result: {
                    WARNING_LOOP_DETECTED: loopState.warning,
                    cachedResultFromStepsAgo: loopState.priorCount,
                    ...((typeof cachedContent === 'object' && cachedContent !== null)
                        ? cachedContent
                        : { cachedText: cachedContent }),
                },
                isError: false,
                error: false,
            };
        }
        if (loopState?.action === 'block') {
            debugLog(`🔄 LOOP BLOCK: ${toolName} (priorCount=${loopState.priorCount}) — refusing to execute`);
            return {
                sessionId: "",
                toolCallId,
                toolName,
                result: {
                    success: false,
                    BLOCKED_LOOP: true,
                    error: loopState.reason,
                },
                isError: true,
                error: true,
            };
        }

        switch (toolName) {
            case "read_attachment":
                result = await getAttachmentContent(p.id);
                break;

            case TOOL_NAMES.GET_RANGE_AS_JSON: {
                // Fix: DeepSeek/Kimi sometimes stringify the ranges array
                let ranges = p.ranges;
                if (typeof ranges === 'string') {
                    try { ranges = JSON.parse(ranges); } catch { /* keep as-is */ }
                }
                if (!Array.isArray(ranges)) {
                    ranges = [String(ranges)];
                }
                result = await getRangeAsJsonCompact(p.sheetId, ranges, {
                    includeStyles: p.includeStyles,
                    cellLimit: p.cellLimit,
                });
                break;
            }

            case TOOL_NAMES.GET_RANGE_AS_CSV:
                result = await getRangeAsCsv(p.sheetId, p.range, {
                    includeHeaders: p.includeHeaders,
                    maxRows: p.maxRows,
                });
                break;

            case TOOL_NAMES.SEARCH_DATA:
                result = await searchData(p.searchTerm, {
                    sheetId: p.sheetId,
                    maxResults: p.maxResults,
                });
                break;

            case TOOL_NAMES.GET_ALL_OBJECTS:
                result = await getAllObjects({ sheetId: p.sheetId, id: p.id });
                break;

            case TOOL_NAMES.SET_CELL_RANGE:
                validateCellsParam('set_cell_range', p.cells, 'array');
                result = await setCellRange(p.sheetId, p.range, p.cells, {
                    allowOverwrite: forceOverwrite || p.allowOverwrite,
                });
                break;

            case TOOL_NAMES.WRITE_CELLS:
                validateCellsParam('write_cells', p.cells, 'object');
                result = await writeCells(p.cells as Record<string, { value?: unknown; formula?: string; numberFormat?: string }>, p.sheetId, {
                    allowOverwrite: forceOverwrite
                });
                break;

            case TOOL_NAMES.CLEAR_CELL_RANGE:
                result = await clearCellRange(p.sheetId, p.range, p.clearType);
                break;

            case TOOL_NAMES.COPY_TO:
                result = await copyTo(p.sheetId, p.sourceRange, p.destinationRange);
                break;

            case TOOL_NAMES.MODIFY_SHEET_STRUCTURE:
                result = await modifySheetStructure(p.sheetId, {
                    operation: p.operation,
                    dimension: p.dimension,
                    reference: p.reference,
                    count: p.count,
                    position: p.position,
                });
                break;

            case TOOL_NAMES.MODIFY_WORKBOOK_STRUCTURE:
                result = await modifyWorkbookStructure({
                    operation: p.operation,
                    sheetId: p.sheetId,
                    sheetName: p.sheetName,
                    newName: p.newName,
                    tabColor: p.tabColor,
                });
                break;
            
            case TOOL_NAMES.IMPORT_DATA:
                result = await importData(p.sheetName, p.data, p.hasHeaders, p.sheetId);
                break;

            case TOOL_NAMES.INSERT_BATCH: {
                const PAGE_SIZE = 10_000;
                const tableIndex = Number.isInteger(p.tableIndex) ? p.tableIndex : 0;

                // structured_data layout: index 0 = header row, indices 1+ = data rows (1-indexed).
                // rowOffset = how many DATA rows to skip (0 = start from first data row).
                // rowLimit  = max DATA rows to include (omit = all remaining).
                const dataOffset = (typeof p.rowOffset === 'number' && p.rowOffset >= 0) ? p.rowOffset : 0;
                const dataLimit  = (typeof p.rowLimit  === 'number' && p.rowLimit  >  0) ? p.rowLimit  : Infinity;
                const includeHeader = p.hasHeaders !== false;

                let firstRows: string[][];

                if (dataOffset === 0) {
                    // Full import from top: first fetch includes the header row at DB index 0
                    const fetchLimit = dataLimit === Infinity ? PAGE_SIZE : Math.min(PAGE_SIZE, dataLimit + 1); // +1 for header
                    const firstPage = await getAttachmentStructuredDataPage(p.attachmentId, tableIndex, 0, fetchLimit);
                    if (!firstPage || firstPage.rows.length === 0) {
                        throw new Error(`Attachment ${p.attachmentId} has no importable structured table at index ${tableIndex}`);
                    }
                    firstRows = firstPage.rows;

                    const importResult = await importData(p.sheetName, firstRows, includeHeader, p.sheetId);

                    // Paginate remaining rows only when no rowLimit is set
                    if (dataLimit === Infinity && firstPage.hasMore) {
                        let nextStartRow = firstRows.length + 1;
                        let dbOffset = fetchLimit;
                        while (dbOffset < firstPage.totalRows) {
                            const page = await getAttachmentStructuredDataPage(p.attachmentId, tableIndex, dbOffset, PAGE_SIZE);
                            if (!page || page.rows.length === 0) break;
                            await appendRowsToSheet(importResult.sheetId, page.rows, nextStartRow);
                            nextStartRow += page.rows.length;
                            dbOffset     += page.rows.length;
                            if (!page.hasMore) break;
                        }
                    }

                    result = { ...importResult, rowsImported: firstPage.totalRows - 1, totalRows: firstPage.totalRows };
                } else {
                    // Partial import: fetch header separately, then fetch the requested data slice
                    const [headerPage, dataPage] = await Promise.all([
                        getAttachmentStructuredDataPage(p.attachmentId, tableIndex, 0, 1),             // header row only
                        getAttachmentStructuredDataPage(p.attachmentId, tableIndex, dataOffset + 1,    // +1 to skip header in DB
                            dataLimit === Infinity ? PAGE_SIZE : dataLimit),
                    ]);
                    if (!dataPage || dataPage.rows.length === 0) {
                        throw new Error(`Attachment ${p.attachmentId}: no rows at offset ${dataOffset}`);
                    }
                    // Combine header + data slice for importData
                    const headerRow = includeHeader && headerPage?.rows[0] ? [headerPage.rows[0]] : [];
                    firstRows = [...headerRow, ...dataPage.rows];

                    const importResult = await importData(p.sheetName, firstRows, includeHeader, p.sheetId);
                    result = { ...importResult, rowsImported: dataPage.rows.length, totalRows: dataPage.totalRows };
                }
                break;
            }

            case TOOL_NAMES.RESIZE_RANGE:
                result = await resizeRange(p.sheetId, {
                    range: p.range,
                    width: p.width,
                    height: p.height,
                });
                break;

            case TOOL_NAMES.MODIFY_OBJECT:
                result = await modifyObject({
                    operation: p.operation,
                    sheetId: p.sheetId,
                    objectType: p.objectType,
                    id: p.id,
                    properties: p.properties,
                });
                break;

            case TOOL_NAMES.SCREENSHOT_RANGE:
                result = await executeScreenshot(p.sheetId, p.range);
                break;

            case TOOL_NAMES.ANALYZE_SHEET_STRUCTURE:
                result = await analyzeSheetStructureForLLM(p.sheetId);
                break;
            

            case TOOL_NAMES.SUMMARIZE_WORKBOOK:
                result = await summarizeWorkbook(p.maxSampleRows);
                break;

            case TOOL_NAMES.EVAL_OFFICEJS:
                result = await executeOfficeJs(p.code);
                break;

            default:
                throw new Error(`Unknown tool: ${toolName}`);
        }

        const elapsed = Date.now() - startTime;
        const resultStr = JSON.stringify(result ?? { success: true }).slice(0, 200);
        debugLog(`✅ DONE: ${toolName} (${elapsed}ms) → ${resultStr}...`);
        if (result === undefined) result = { success: true };

        return {
            sessionId: "", // filled in by the caller
            toolCallId,
            toolName,
            result,
            isError: false,
            error: false,
        };
    } catch (err: unknown) {
        const elapsed = Date.now() - startTime;
        const errorPayload = extractStructuredError(err, toolName);
        debugLog(`❌ FAIL: ${toolName} (${elapsed}ms) → ${errorPayload.message}`);
        console.error(`[ToolExecutor] ${toolName} failed:`, err);
        return {
            sessionId: "",
            toolCallId,
            toolName,
            result: { success: false, error: errorPayload },
            isError: true,
            error: true,
        };
    }
}

/**
 * Extract a structured error payload from an arbitrary thrown value.
 *
 * For Office.js failures (`OfficeExtension.Error`), this preserves the
 * `code`, `debugInfo` (including `errorLocation` and `fullStatements`),
 * and `traceMessages` — all of which are crucial for the model to figure
 * out what actually went wrong.
 *
 * Prior implementation extracted only `.message`, which collapsed
 * actionable errors like "unquoted sheet name with space" into the
 * generic "The argument is invalid or missing or has an incorrect format."
 * See feedback_blame_tools_before_blaming_model.md.
 */
function extractStructuredError(err: unknown, toolName: string): Record<string, unknown> {
    if (err instanceof Error) {
        const anyErr = err as any;
        const payload: Record<string, unknown> = {
            name: err.name,
            message: err.message,
        };
        if (anyErr.code) payload.code = anyErr.code;
        if (anyErr.debugInfo) payload.debugInfo = anyErr.debugInfo;
        if (Array.isArray(anyErr.traceMessages) && anyErr.traceMessages.length) {
            payload.traceMessages = anyErr.traceMessages.slice(0, 5);
        }
        return payload;
    }
    if (typeof err === "string") return { message: err };
    if (err && typeof err === "object") return err as Record<string, unknown>;
    return { message: `Tool execution failed: ${toolName}` };
}

/**
 * Read current workbook metadata for injection into the chat request.
 */
export { getWorkbookMetadata };

/**
 * Evaluate a code string with the given variables in scope.
 * Uses the Function constructor so that `context` and `Excel` are available
 * as named locals inside the evaluated code.
 */
async function sandboxedEval(code: string, scope: Record<string, unknown>): Promise<unknown> {
    const keys = Object.keys(scope);
    const values = Object.values(scope);
    // eslint-disable-next-line no-new-func
    const fn = new Function(...keys, `return (async () => { ${code} })()`);
    return fn(...values);
}

/**
 * Execute arbitrary Office.js code (the eval_officejs tool escape hatch).
 */
async function executeOfficeJs(code: string): Promise<unknown> {
    const { createTrackedContext } = await import("../excel/tracked-context");

    return Excel.run(async (context) => {
        const { trackedContext } = createTrackedContext(context);
        return await sandboxedEval(code, {
            context: trackedContext,
            Excel,
        });
    });
}

const HEADER_WIDTH = 40;
const HEADER_HEIGHT = 20;
const HEADER_BG = "#f0f0f0";
const HEADER_BORDER = "#c0c0c0";
const HEADER_FONT = "bold 11px Calibri, Arial, sans-serif";
const HEADER_TEXT_COLOR = "#333333";

function columnIndexToLetter(index: number): string {
    let letter = "";
    let temp = index;
    while (temp >= 0) {
        letter = String.fromCharCode((temp % 26) + 65) + letter;
        temp = Math.floor(temp / 26) - 1;
    }
    return letter;
}

async function executeScreenshot(sheetId: number, rangeAddr: string): Promise<unknown> {
    const parsed = parseRange(rangeAddr);
    if (!parsed) throw new Error(`Invalid range: ${rangeAddr}`);

    const { startRow, startCol, endCol, endRow } = parsed;
    const numCols = endCol - startCol + 1;
    const numRows = endRow - startRow + 1;

    const data = await Excel.run(async (context) => {
        const sheet = await getWorksheetById(context, sheetId);
        if (!sheet) throw new Error(`Worksheet with ID ${sheetId} not found`);

        const range = sheet.getRange(rangeAddr);
        const image = range.getImage();

        const cols: Excel.Range[] = [];
        for (let i = 0; i < numCols; i++) {
            const col = range.getColumn(i);
            col.format.load("columnWidth");
            cols.push(col);
        }

        const rows: Excel.Range[] = [];
        for (let i = 0; i < numRows; i++) {
            const row = range.getRow(i);
            row.format.load("rowHeight");
            rows.push(row);
        }

        await context.sync();

        return {
            imageBase64: image.value,
            colWidths: cols.map((c) => c.format.columnWidth),
            rowHeights: rows.map((r) => r.format.rowHeight),
        };
    });

    const compositeBase64 = await compositeWithHeaders(
        data.imageBase64,
        startRow,
        startCol,
        data.colWidths,
        data.rowHeights,
    );

    const resized = await resizeImage(compositeBase64, "image/png");

    return {
        content: [
            { type: "text", text: `Screenshot of ${rangeAddr}` },
            { type: "image", data: resized.data, mimeType: resized.mimeType },
        ],
    };
}

function compositeWithHeaders(
    imageBase64: string,
    startRow: number,
    startCol: number,
    colWidths: number[],
    rowHeights: number[],
): Promise<string> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const totalColWidth = colWidths.reduce((a, b) => a + b, 0);
            const totalRowHeight = rowHeights.reduce((a, b) => a + b, 0);
            const scaleX = totalColWidth > 0 ? img.width / totalColWidth : 1;
            const scaleY = totalRowHeight > 0 ? img.height / totalRowHeight : 1;

            const canvas = document.createElement("canvas");
            canvas.width = HEADER_WIDTH + img.width;
            canvas.height = HEADER_HEIGHT + img.height;

            const ctx = canvas.getContext("2d");
            if (!ctx) return reject(new Error("Failed to get 2d canvas context"));

            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, HEADER_WIDTH, HEADER_HEIGHT);

            ctx.fillStyle = HEADER_BG;
            ctx.fillRect(HEADER_WIDTH, 0, img.width, HEADER_HEIGHT);
            ctx.strokeStyle = HEADER_BORDER;
            ctx.font = HEADER_FONT;
            ctx.fillStyle = HEADER_TEXT_COLOR;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            let x = HEADER_WIDTH;
            for (let i = 0; i < colWidths.length; i++) {
                const w = colWidths[i] * scaleX;
                ctx.strokeStyle = HEADER_BORDER;
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, HEADER_HEIGHT);
                ctx.stroke();
                ctx.fillText(columnIndexToLetter(startCol + i), x + w / 2, HEADER_HEIGHT / 2);
                x += w;
            }

            ctx.fillStyle = HEADER_BG;
            ctx.fillRect(0, HEADER_HEIGHT, HEADER_WIDTH, img.height);
            let y = HEADER_HEIGHT;
            for (let i = 0; i < rowHeights.length; i++) {
                const h = rowHeights[i] * scaleY;
                ctx.strokeStyle = HEADER_BORDER;
                ctx.beginPath();
                ctx.moveTo(0, y);
                ctx.lineTo(HEADER_WIDTH, y);
                ctx.stroke();
                ctx.fillStyle = HEADER_TEXT_COLOR;
                ctx.fillText(String(startRow + i + 1), HEADER_WIDTH / 2, y + h / 2);
                y += h;
            }

            ctx.fillStyle = HEADER_BG;
            ctx.fillRect(0, 0, HEADER_WIDTH, HEADER_HEIGHT);
            ctx.strokeStyle = HEADER_BORDER;
            ctx.strokeRect(0, 0, HEADER_WIDTH, HEADER_HEIGHT);
            ctx.strokeRect(HEADER_WIDTH, 0, img.width, HEADER_HEIGHT);
            ctx.strokeRect(0, HEADER_HEIGHT, HEADER_WIDTH, img.height);

            resolve(canvas.toDataURL("image/png").split(",")[1]);
        };
        img.onerror = () => reject(new Error("Failed to load range image"));
        img.src = `data:image/png;base64,${imageBase64}`;
    });
}

// ─── Remote Execution ───

const REMOTE_REQ_KEY = "excel-agent-remote-req";
const REMOTE_RES_KEY = "excel-agent-remote-res";

async function executeRemoteTool(
    targetInstanceId: string,
    event: SseToolCallEvent,
    forceOverwrite: boolean
): Promise<ToolResultRequest & { error: boolean }> {
    const reqId = crypto.randomUUID();
    const request = {
        reqId,
        targetInstanceId,
        event,
        forceOverwrite,
        timestamp: Date.now()
    };

    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            window.removeEventListener("storage", handler);
            // Re-check liveness: if the workbook is gone now it closed while
            // we were waiting — give a WORKBOOK_CLOSED code so the LLM knows
            // not to retry rather than treating it as a transient timeout.
            const isGone = !getConnectedWorkbooks().some(wb => wb.id === targetInstanceId);
            const err = new Error(
                isGone
                    ? `Workbook (ID: ${targetInstanceId}) closed while the tool was executing. Do not retry.`
                    : `Remote execution timeout for workbook ${targetInstanceId}`
            ) as any;
            if (isGone) err.code = "WORKBOOK_CLOSED";
            reject(err);
        }, 30000);

        const handler = (e: StorageEvent) => {
            if (e.key === REMOTE_RES_KEY && e.newValue) {
                try {
                    const response = JSON.parse(e.newValue);
                    if (response.reqId === reqId) {
                        clearTimeout(timeout);
                        window.removeEventListener("storage", handler);
                        resolve(response.result);
                    }
                } catch (err) {
                    console.error("Failed to parse remote response:", err);
                }
            }
        };

        window.addEventListener("storage", handler);
        localStorage.setItem(REMOTE_REQ_KEY, JSON.stringify(request));
        // Remove it immediately so it can be triggered again with the same data if needed
        localStorage.removeItem(REMOTE_REQ_KEY);
    });
}

/** Listen for tool requests from other workbooks */
export function initRemoteToolListener() {
    window.addEventListener("storage", async (e) => {
        if (e.key === REMOTE_REQ_KEY && e.newValue) {
            try {
                const request = JSON.parse(e.newValue);
                if (request.targetInstanceId === instanceId) {
                    debugLog(`🌐 RECEIVED REMOTE REQ: ${request.event.toolName}`);
                    
                    // Execute locally (recursive call to executeTool, but with targetWorkbookId matched)
                    const result = await executeTool(request.event, request.forceOverwrite);
                    
                    const response = {
                        reqId: request.reqId,
                        result
                    };
                    localStorage.setItem(REMOTE_RES_KEY, JSON.stringify(response));
                    // Cleanup
                    localStorage.removeItem(REMOTE_RES_KEY);
                }
            } catch (err) {
                console.error("Failed to handle remote request:", err);
            }
        }
    });
}
